import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Check, ChevronRight, CloudDownload, FileUp, Loader2, Mail, X } from "lucide-react";
import { importSources, importUpload, importRemap, importFileBatch, importConnector, importGoogleStart, importGoogleStatus, importConnectStart, importConnectStatus, saveKeys } from "../lib/api";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };
const KIND_LABEL = { contacts: "Contacts (people)", companies: "Companies", notes: "Notes" };
const STAT = {
  inserted: "Added", updated: "Filled in", unchanged: "Already up to date", skipped: "Skipped (no name or email)",
  dialer_added: "Phone numbers added to Calls", invalid_phones: "Unusable phone numbers",
  attached: "Notes added", already_imported: "Notes already there", no_matching_record: "Notes with no matching contact",
};
const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const addUp = (a, b) => { const o = { ...a }; Object.entries(b || {}).forEach(([k, v]) => { if (typeof v === "number" && k !== "total_rows" && k !== "next_offset") o[k] = (o[k] || 0) + v; }); return o; };

function Stats({ result }) {
  const rows = Object.entries(result || {}).filter(([k, v]) => STAT[k] && v);
  return rows.length ? <div className="apollo-stats summary-stats">{rows.map(([k, v]) => <span key={k}><b>{v}</b><small>{STAT[k]}</small></span>)}</div>
    : <small className="muted">Nothing new — everything was already here.</small>;
}

// Paste the developer app's client ID and secret right on the card (same place Settings keeps them), then Sign in works.
function CredentialsForm({ idKey, secretKey, label, help, redirectUri, onSaved }) {
  const [id, setId] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await saveKeys({ [idKey]: id.trim(), [secretKey]: secret.trim() }); toast.success(`${label} saved`); onSaved(); }
    catch (e) { toast.error(errText(e, "Couldn't save")); }
    finally { setBusy(false); }
  };
  return <div className="creds-form">
    <small>{help} Redirect URL to register: <code>{redirectUri}</code></small>
    <input placeholder="Client ID" value={id} onChange={e => setId(e.target.value)} autoComplete="off" />
    <input placeholder="Client secret" type="password" value={secret} onChange={e => setSecret(e.target.value)} autoComplete="off" />
    <button className="outline-button" onClick={save} disabled={busy || !id.trim() || !secret.trim()}>{busy ? <Loader2 size={14} className="spin" /> : "Save"}</button>
  </div>;
}

// Upload a spreadsheet: preview what it is, check the columns, import in batches.
function FileImport({ sources, onImported }) {
  const [file, setFile] = useState(null);
  const [up, setUp] = useState(null);
  const [kind, setKind] = useState("contacts");
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const pick = async (f) => {
    if (!f) return;
    setError(""); setResult(null); setFile(f); setBusy("read");
    try {
      const r = await importUpload(f.name, await readAsBase64(f));
      setUp(r); setKind(r.kind); setMapping(r.mapping);
    } catch (e) { setError(errText(e, "Couldn't read that file")); setUp(null); }
    finally { setBusy(""); if (inputRef.current) inputRef.current.value = ""; }
  };
  const changeKind = async (k) => {
    setKind(k);
    try { setMapping((await importRemap(up.upload_id, k, up.format)).mapping); } catch (e) { setError(errText(e, "Couldn't change that")); }
  };
  const run = async () => {
    setBusy("import"); setError(""); setResult(null);
    let total = {}, offset = 0;
    try {
      while (offset !== null) {
        setProgress(`${Math.min(offset + 1000, up.total_rows).toLocaleString()} of ${up.total_rows.toLocaleString()} rows`);
        const r = await importFileBatch({ upload_id: up.upload_id, kind, format: up.format, mapping, offset });
        total = addUp(total, r);
        offset = r.next_offset;
      }
      setResult(total); onImported?.();
      toast.success(`Imported ${up.total_rows.toLocaleString()} rows from ${file.name}`);
    } catch (e) { setError(errText(e, "The import stopped partway — run it again; nothing is duplicated")); if (Object.keys(total).length) setResult(total); }
    finally { setBusy(""); setProgress(""); }
  };

  const targets = sources.targets[kind] || [];
  const colsUsed = new Set(Object.values(mapping).filter(Boolean));
  return <div className="panel import-panel" data-testid="import-file">
    <h3><FileUp size={16} /> Excel or CSV file</h3>
    <p className="muted">Works with exports from {sources.files.filter(f => f.key !== "generic").map(f => f.label).join(", ")}, or any spreadsheet. Importing the same file again never creates duplicates or overwrites your edits.</p>
    {!up && <div className="dropzone" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }} data-testid="import-dropzone">
      <FileUp size={23} /><b>Drop a .xlsx or .csv file here</b><span>or choose one from your computer</span>
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.csv,.tsv,.txt" hidden onChange={e => pick(e.target.files?.[0])} data-testid="import-file-input" />
      <button className="outline-button" onClick={() => inputRef.current?.click()} disabled={busy === "read"}>{busy === "read" ? <><Loader2 size={14} className="spin" /> Reading…</> : "Choose file"}</button>
    </div>}
    {up && <>
      <div className="import-file-head">
        <div><b>{file?.name}</b><small>{up.total_rows.toLocaleString()} rows · looks like a {up.format_label} export</small></div>
        <label>This file has
          <select value={kind} onChange={e => changeKind(e.target.value)} disabled={!!busy} data-testid="import-kind">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <button className="icon-button small" title="Choose another file" onClick={() => { setUp(null); setResult(null); }} disabled={!!busy}><X size={14} /></button>
      </div>
      <div className="mapping-grid" data-testid="import-mapping">
        {targets.map(t => <div key={t.key} className="mapping-row"><label>{t.label}</label>
          <select value={mapping[t.key] || ""} onChange={e => setMapping({ ...mapping, [t.key]: e.target.value })} disabled={!!busy}>
            <option value="">— Skip —</option>
            {up.headers.map(h => <option key={h} value={h} disabled={colsUsed.has(h) && mapping[t.key] !== h}>{h}</option>)}
          </select></div>)}
      </div>
      <small className="muted">Columns you leave out are kept with each record, so nothing from the file is lost.</small>
      <div className="preview-block"><b>First rows</b><div className="preview-table-wrap"><table>
        <thead><tr>{targets.filter(t => mapping[t.key]).map(t => <th key={t.key}>{t.label}</th>)}</tr></thead>
        <tbody>{up.sample.map((r, i) => <tr key={i}>{targets.filter(t => mapping[t.key]).map(t => <td key={t.key}>{r[mapping[t.key]] || <span className="muted">—</span>}</td>)}</tr>)}</tbody>
      </table></div></div>
      <div className="modal-actions">
        {progress && <span className="muted"><Loader2 size={14} className="spin" /> Importing {progress}…</span>}
        <button className="primary-button" onClick={run} disabled={!!busy} data-testid="import-run">{busy === "import" ? "Importing…" : <>Import {up.total_rows.toLocaleString()} rows <ChevronRight size={14} /></>}</button>
      </div>
    </>}
    {error && <div className="error-strip"><AlertCircle size={14} /> {error}</div>}
    {result && <div className="import-summary" data-testid="import-result"><div className="summary-hero"><Check size={20} /><div><b>Import finished</b></div></div><Stats result={result} /></div>}
  </div>;
}

export const SHOW_GOOGLE = false; // Google contacts sign-in is switched off for now. Set to true to bring it back.

export function GoogleCard({ google, onImported }) {
  const [ready, setReady] = useState(google.configured);
  const [editing, setEditing] = useState(false);
  const [includeEmail, setIncludeEmail] = useState(true);
  const [state, setState] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!state) return undefined;
    const started = Date.now();
    const t = setInterval(async () => {
      try {
        const s = await importGoogleStatus(state);
        if (s.status === "done") { clearInterval(t); setState(null); setResult(s); onImported?.(); toast.success("Imported from Google"); }
        else if (s.status === "error" || s.status === "expired") { clearInterval(t); setState(null); toast.error(s.detail || "The Google sign-in expired — try again"); }
        else if (Date.now() - started > 10 * 60 * 1000) { clearInterval(t); setState(null); }
      } catch { /* keep waiting */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const start = async () => {
    try { const r = await importGoogleStart(includeEmail); setState(r.state); window.open(r.url, "_blank", "noopener"); }
    catch (e) { toast.error(errText(e, "Couldn't start the Google sign-in")); }
  };
  return <div className="source-card import-source" data-testid="import-google">
    <div className="source-logo mailer"><Mail size={16} /></div>
    <div><b>Google Contacts & Gmail</b>
      <small>{ready ? "Sign in with Google; your contacts are imported straight away." : "Needs a Google Cloud OAuth client (console.cloud.google.com → Credentials → OAuth client ID, Web application)."}</small>
      {ready && !editing && <button className="link-button" onClick={() => setEditing(true)}>Change client ID / secret</button>}
      {(!ready || editing) && <CredentialsForm idKey="GOOGLE_CLIENT_ID" secretKey="GOOGLE_CLIENT_SECRET" label="Google" help="Paste both values from your Google OAuth client." redirectUri={google.redirect_uri} onSaved={() => { setReady(true); setEditing(false); }} />}
      <label className="verify-toggle"><input type="checkbox" checked={includeEmail} onChange={e => setIncludeEmail(e.target.checked)} />
        <span>Also add people you've emailed (names and addresses only — the CRM never reads your emails)</span></label>
      {result && <small>Added {result.contacts?.inserted || 0} from Contacts{result.emailed ? ` · ${result.emailed.inserted} from Gmail` : ""}</small>}</div>
    <button className="outline-button" onClick={start} disabled={!ready || !!state}>{state ? <><Loader2 size={14} className="spin" /> Waiting…</> : "Sign in & import"}</button>
  </div>;
}

// A CRM (Zoho, HubSpot, Pipedrive, Salesforce): sign in once, everything is imported straight away; "Import again" refreshes it.
export function ConnectorCard({ c, onImported }) {
  const [ready, setReady] = useState(c.configured || c.connected);
  const [editing, setEditing] = useState(false);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!state) return undefined;
    const started = Date.now();
    const t = setInterval(async () => {
      try {
        const s = await importConnectStatus(c.key, state);
        if (s.status === "done") { clearInterval(t); setState(null); setResult(s); c.connected = true; onImported?.(); toast.success(`Imported from ${c.label}`); }
        else if (s.status === "error" || s.status === "expired") { clearInterval(t); setState(null); toast.error(s.detail || `The ${c.label} sign-in expired — try again`); }
        else if (Date.now() - started > 10 * 60 * 1000) { clearInterval(t); setState(null); }
      } catch { /* keep waiting */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const signIn = async () => {
    try { const r = await importConnectStart(c.key); setState(r.state); window.open(r.url, "_blank", "noopener"); }
    catch (e) { toast.error(errText(e, `Couldn't start the ${c.label} sign-in`)); }
  };
  const again = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await importConnector(c.key);
      setResult(r); onImported?.();
      toast.success(`${c.label}: ${r.contacts.inserted} new contacts, ${r.companies.inserted} new companies`);
    } catch (e) { toast.error(errText(e, `Couldn't import from ${c.label}`)); }
    finally { setBusy(false); }
  };
  return <div className="source-card import-source" data-testid={`import-connector-${c.key}`}>
    <div className="source-logo csv"><CloudDownload size={16} /></div>
    <div><b>{c.label}</b>
      <small>{!ready ? "Needs a one-time setup: paste your developer app's client ID and secret below."
        : c.connected ? "Connected — contacts, companies and notes come in; importing again only fills in blanks."
        : "Sign in; contacts, companies and notes are imported straight away."}</small>
      {ready && !editing && <button className="link-button" onClick={() => setEditing(true)}>Change client ID / secret</button>}
      {(!ready || editing) && <CredentialsForm idKey={c.keys[0]} secretKey={c.keys[1]} label={c.label} help={c.help} redirectUri={c.redirect_uri} onSaved={() => { setReady(true); setEditing(false); }} />}
      {result && <small>Added {result.contacts?.inserted || 0} contacts · {result.companies?.inserted || 0} companies · {result.notes?.attached || 0} notes</small>}</div>
    <div className="toolbar-actions">
      {c.connected && <button className="outline-button" onClick={again} disabled={busy || !!state}>{busy ? <Loader2 size={14} className="spin" /> : "Import again"}</button>}
      <button className="outline-button" onClick={signIn} disabled={!ready || !!state}>{state ? <><Loader2 size={14} className="spin" /> Waiting…</> : c.connected ? "Reconnect" : "Sign in & import"}</button>
    </div>
  </div>;
}

export default function ImportView({ onImported }) {
  const [sources, setSources] = useState(null);
  useEffect(() => { importSources().then(setSources).catch(() => toast.error("Couldn't load the import options")); }, []);
  if (!sources) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading…</div></div>;
  return <div className="page-view" data-testid="import-view">
    <div className="view-toolbar"><div><div className="eyebrow"><span className="eyebrow-dot" /> BRING YOUR CUSTOMERS</div><h2>Import</h2>
      <p>From a spreadsheet, another CRM or Google. Everything lands in Contacts and Companies, matched so nobody appears twice.</p></div></div>
    <FileImport sources={sources} onImported={onImported} />
    <div className="panel import-panel">
      <h3><CloudDownload size={16} /> Straight from your accounts</h3>
      <p className="muted">Add each one's key in Settings once, then import whenever you like. Salesforce: export a report or Data Export as CSV and use the file import above.</p>
      {SHOW_GOOGLE && <GoogleCard google={sources.google} onImported={onImported} />}
      {sources.connectors.map(c => <ConnectorCard key={c.key} c={c} onImported={onImported} />)}
    </div>
  </div>;
}
